import { db } from '../firebase';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  onSnapshot,
  orderBy,
  where,
  getDocs,
  Timestamp,
  arrayUnion,
  arrayRemove,
  getDoc,
  setDoc,
  writeBatch,
  runTransaction
} from 'firebase/firestore';
import { processNoteForSaving, processNoteForDisplay } from '../utils/noteEncryption';
import { log, logError } from '../utils/logger';

// Collection references
const notesCollection = collection(db, 'notes');
const usersCollection = collection(db, 'users');

// Add a new note
export const addNote = async (note) => {
  try {
    // Get all notes to find the highest order
    const q = query(
      notesCollection,
      where("userId", "==", note.userId)
    );
    const querySnapshot = await getDocs(q);
    let maxOrder = 0;
    querySnapshot.forEach((doc) => {
      const noteData = doc.data();
      maxOrder = Math.max(maxOrder, noteData.order || 0);
    });

    // Process note for saving (encrypt if enabled)
    const processedNote = await processNoteForSaving({
      ...note,
      order: maxOrder + 1000,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });

    const docRef = await addDoc(notesCollection, processedNote);
    log("Note added successfully");

    // Update user's tags
    if (note.tags && note.tags.length > 0) {
      await updateUserTags(note.userId, note.tags);
    }

    return { ...processedNote, id: docRef.id };
  } catch (error) {
    logError("Error adding note", error);
    throw error;
  }
};

// Import all notes in one Firestore commit so a failed import never leaves a partial set behind.
export const importNotesAtomically = async (userId, notes, startingOrder = 0) => {
  if (notes.length > 500) {
    const error = new Error('A maximum of 500 notes can be imported at once.');
    error.code = 'notes/import-limit';
    throw error;
  }

  try {
    const preparedNotes = await Promise.all(notes.map(async (note, index) => processNoteForSaving({
      ...note,
      userId,
      order: startingOrder + ((index + 1) * 1000),
      isArchived: false,
      pinned: false,
      category: note.category || 'Uncategorized',
      tags: Array.isArray(note.tags) ? note.tags : [],
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    })));
    const importedTags = [...new Set(preparedNotes.flatMap(note => note.tags || []))];

    // Keep imported notes and the profile tag register in one commit. A batch has
    // room for 500 operations, so an import with tags reserves one operation for
    // the user profile.
    if (importedTags.length > 0 && preparedNotes.length > 499) {
      const error = new Error('A maximum of 499 tagged notes can be imported at once.');
      error.code = 'notes/import-tag-limit';
      throw error;
    }

    const batch = writeBatch(db);

    preparedNotes.forEach(note => {
      batch.set(doc(notesCollection), note);
    });

    if (importedTags.length > 0) {
      batch.set(doc(usersCollection, userId), {
        tags: arrayUnion(...importedTags)
      }, { merge: true });
    }
    await batch.commit();
    log(`Imported ${preparedNotes.length} notes successfully`);
    return preparedNotes;
  } catch (error) {
    logError('Error importing notes', error);
    throw error;
  }
};

// Update a note
export const updateNote = async (noteId, noteData) => {
  try {
    const noteRef = doc(db, 'notes', noteId);
    
    // Process note for saving (encrypt if enabled)
    const processedNote = await processNoteForSaving({
      ...noteData,
      updatedAt: Timestamp.now()
    });

    await updateDoc(noteRef, processedNote);
    log("Note updated successfully");

    // Update user's tags
    if (noteData.tags && noteData.userId) {
      await updateUserTags(noteData.userId, noteData.tags);
    }

    return { ...processedNote, id: noteId };
  } catch (error) {
    logError("Error updating note", error);
    throw error;
  }
};

// Reassign notes and remove their category from the user's profile atomically.
export const deleteCategoryAndReassignNotes = async (userId, notesToReassign, remainingCategories) => {
  try {
    if (notesToReassign.length > 499) {
      const error = new Error('Categories with more than 499 notes cannot be moved atomically.');
      error.code = 'category/reassignment-limit';
      throw error;
    }

    const batch = writeBatch(db);

    notesToReassign.forEach((note) => {
      batch.update(doc(db, 'notes', note.id), {
        category: 'Uncategorized',
        updatedAt: Timestamp.now()
      });
    });

    batch.update(doc(db, 'users', userId), { categories: remainingCategories });
    await batch.commit();
    log('Category deleted and notes reassigned successfully');
  } catch (error) {
    logError('Error deleting category and reassigning notes', error);
    throw error;
  }
};

// Rename a category and every matching note from fresh reads in one batch.
export const renameCategoryAndUpdateNotes = async (userId, previousCategoryName, nextCategory) => {
  try {
    const userRef = doc(db, 'users', userId);
    const notesQuery = query(
      notesCollection,
      where('userId', '==', userId),
      where('category', '==', previousCategoryName)
    );

    const [userSnapshot, notesSnapshot] = await Promise.all([
      getDoc(userRef),
      getDocs(notesQuery)
    ]);
    const currentCategories = userSnapshot.exists() ? (userSnapshot.data().categories || []) : [];

    if (!currentCategories.some(category => category.name === previousCategoryName)) {
      const error = new Error('The category was changed on another device.');
      error.code = 'category/rename-conflict';
      throw error;
    }

    if (notesSnapshot.docs.length > 499) {
      const error = new Error('Categories with more than 499 notes cannot be renamed atomically.');
      error.code = 'category/rename-limit';
      throw error;
    }

    const nextCategories = currentCategories.map(category =>
      category.name === previousCategoryName ? nextCategory : category
    );
    const batch = writeBatch(db);

    notesSnapshot.docs.forEach((noteDoc) => {
      batch.update(noteDoc.ref, {
        category: nextCategory.name,
        updatedAt: Timestamp.now()
      });
    });

    batch.update(userRef, { categories: nextCategories });
    await batch.commit();
    log('Category renamed and notes updated successfully');
  } catch (error) {
    logError('Error renaming category and updating notes', error);
    throw error;
  }
};

// Delete a note
export const deleteNote = async (noteId) => {
  try {
    const noteRef = doc(db, 'notes', noteId);
    await deleteDoc(noteRef);
    log("Note deleted successfully");
    return noteId;
  } catch (error) {
    logError("Error deleting note", error);
    throw error;
  }
};

// Get all notes for a user
export const getNotes = async (userId) => {
  try {
    const q = query(
      notesCollection,
      where("userId", "==", userId),
      orderBy("pinned", "desc"),
      orderBy("order", "asc"),
      orderBy("__name__", "asc")
    );
    const querySnapshot = await getDocs(q);
    const notes = [];
    querySnapshot.forEach((doc) => {
      const note = { id: doc.id, ...doc.data() };
      notes.push(note);
    });
    return notes;
  } catch (error) {
    logError("Error getting notes", error);
    throw error;
  }
};

// Subscribe to notes updates with ordering
export const subscribeToNotes = (userId, callback) => {
  log("Subscribing to notes for user");
  
  if (!userId) {
    logError("No userId provided to subscribeToNotes");
    callback([]);
    return () => {};
  }

  const q = query(
    notesCollection,
    where("userId", "==", userId),
    orderBy("pinned", "desc"),
    orderBy("order", "asc"),
    orderBy("__name__", "asc")
  );

  const unsubscribe = onSnapshot(q, 
    async (snapshot) => {
      log("Received snapshot with changes");
      const notes = [];
      for (const doc of snapshot.docs) {
        const note = { id: doc.id, ...doc.data() };
        // Process note for display (decrypt if needed)
        const processedNote = await processNoteForDisplay(note);
        notes.push(processedNote);
      }
      log(`Processed ${notes.length} notes`);
      callback(notes);
    }, 
    (error) => {
      logError("Error in notes subscription", error);
      callback([]);
    }
  );

  return unsubscribe;
};

// Get user's tags
export const getUserTags = async (userId) => {
  try {
    const userRef = doc(usersCollection, userId);
    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) {
      return userDoc.data().tags || [];
    }
    return [];
  } catch (error) {
    logError("Error getting user tags", error);
    throw error;
  }
};

// Subscribe to user's tags
export const subscribeToUserTags = (userId, callback) => {
  if (!userId) {
    callback([]);
    return () => {};
  }

  const userRef = doc(usersCollection, userId);
  return onSnapshot(userRef, (doc) => {
    if (doc.exists()) {
      callback(doc.data().tags || []);
    } else {
      callback([]);
    }
  });
};

// Update user's tags
export const updateUserTags = async (userId, newTags) => {
  try {
    const userRef = doc(usersCollection, userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const existingTags = userDoc.data().tags || [];
      const uniqueTags = [...new Set([...existingTags, ...newTags])];
      await updateDoc(userRef, { tags: uniqueTags });
    } else {
      await setDoc(userRef, { tags: newTags });
    }
  } catch (error) {
    logError("Error updating user tags", error);
    throw error;
  }
};

// Remove a tag from user's tags
export const removeUserTag = async (userId, tagToRemove) => {
  try {
    const userRef = doc(db, 'users', userId);
    const q = query(notesCollection, where("userId", "==", userId));

    await runTransaction(db, async (transaction) => {
      const [userDoc, notesSnapshot] = await Promise.all([
        transaction.get(userRef),
        transaction.get(q)
      ]);
      const currentTags = userDoc.data()?.tags || [];
      const updatedTags = currentTags.filter(tag => tag !== tagToRemove);
      const notesToUpdate = notesSnapshot.docs.filter((noteDoc) =>
        (noteDoc.data().tags || []).includes(tagToRemove)
      );

      // The profile update shares the transaction with every note update.
      // Firestore retries this callback if any read document changes.
      if (notesToUpdate.length > 499) {
        const error = new Error('Tags used by more than 499 notes cannot be removed atomically.');
        error.code = 'tag/removal-limit';
        throw error;
      }

      notesToUpdate.forEach((noteDoc) => {
        const noteTags = noteDoc.data().tags || [];
        transaction.update(noteDoc.ref, { tags: noteTags.filter(tag => tag !== tagToRemove) });
      });
      transaction.update(userRef, { tags: updatedTags });
    });

    log('Tag removed successfully from user and notes');
    return true; // Return success to help with UI updates
  } catch (error) {
    logError('Error removing tag', error);
    throw error;
  }
};

// Get user's categories
export const getUserCategories = async (userId) => {
  try {
    const userRef = doc(usersCollection, userId);
    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) {
      return userDoc.data().categories || getDefaultCategories();
    }
    return getDefaultCategories();
  } catch (error) {
    logError("Error getting user categories", error);
    throw error;
  }
};

// Subscribe to user's categories
export const subscribeToUserCategories = (userId, callback) => {
  if (!userId) {
    callback(getDefaultCategories());
    return () => {};
  }

  const userRef = doc(usersCollection, userId);
  return onSnapshot(userRef, (doc) => {
    if (doc.exists()) {
      callback(doc.data().categories || getDefaultCategories());
    } else {
      callback(getDefaultCategories());
    }
  });
};

// Update user's categories
export const updateUserCategories = async (userId, categories) => {
  try {
    const userRef = doc(usersCollection, userId);
    await updateDoc(userRef, { categories });
  } catch (error) {
    logError("Error updating user categories", error);
    throw error;
  }
};

// Create example notes for new users
export const createExampleNotes = async (userId) => {
  try {
    log('Starting to create example notes for user');
    
    const exampleNotes = [
      {
        userId,
        title: "📝 Project Ideas",
        content: "Here are some project ideas for 2024:\n\n" +
                "• Mobile app for fitness tracking\n" +
                "• E-commerce website redesign\n" +
                "• Smart home automation system\n" +
                "• AI-powered task manager\n\n" +
                "Remember to prioritize based on market research!",
        category: "Work/Projects",
        tags: ["projects", "ideas", "planning"],
        pinned: true,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      },
      {
        userId,
        title: "🎯 Weekly Goals",
        content: "Goals for this week:\n\n" +
                "✅ Complete project documentation\n" +
                "✅ Team meeting on Wednesday\n" +
                "⬜ Review pull requests\n" +
                "⬜ Update client presentation\n\n" +
                "Don't forget to update progress daily!",
        category: "Personal",
        tags: ["goals", "tasks", "weekly"],
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      },
      {
        userId,
        title: "💡 Meeting Notes",
        content: "Client Meeting - Website Redesign\n\n" +
                "Key Points:\n" +
                "• Modern, minimalist design\n" +
                "• Mobile-first approach\n" +
                "• Improved user navigation\n" +
                "• Performance optimization\n\n" +
                "Next Steps:\n" +
                "1. Create wireframes\n" +
                "2. Design mockups\n" +
                "3. Client review",
        category: "Work/Projects",
        tags: ["meeting", "client", "design"],
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      }
    ];

    // Add each example note individually with retry logic
    for (const note of exampleNotes) {
      // Wait 2 seconds between each note creation
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      let retries = 3;
      while (retries > 0) {
        try {
          log(`Adding note: ${note.title}`);
          // Use addNote instead of addDoc to ensure proper order handling
          const newNote = await addNote(note);
          log('Note added successfully');
          break; // Success, exit retry loop
        } catch (error) {
          logError(`Error adding note (${retries} retries left)`, error);
          retries--;
          if (retries === 0) throw error;
          // Wait 2 seconds before retry
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
    }

    log("All example notes created successfully");

    // Update user's tags
    const allTags = [...new Set(exampleNotes.flatMap(note => note.tags))];
    await updateUserTags(userId, allTags);
    log("User tags updated with example tags");

  } catch (error) {
    logError("Error creating example notes", error);
    throw error;
  }
};

// Initialize new user
export const initializeNewUser = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    // Only return true if this is actually a new user
    if (!userDoc.exists()) {
      await setDoc(userRef, {
        categories: getDefaultCategories(),
        tags: [],
        createdAt: Timestamp.now()
      });
      return true;
    }
    
    // Existing user, don't show welcome guide
    return false;
  } catch (error) {
    logError('Error initializing user', error);
    return false;
  }
};

// Helper function to get default categories
const getDefaultCategories = () => [
  { name: 'All Notes', color: 'bg-gray-300' },
  { name: 'Personal', color: 'bg-blue-200' },
  { name: 'Work/Projects', color: 'bg-green-200' },
  { name: 'Ideas', color: 'bg-yellow-200' },
  { name: 'Uncategorized', color: 'bg-gray-300' }
];

// Update note order
export const updateNoteOrder = async (noteId, newOrder) => {
  try {
    const noteRef = doc(db, 'notes', noteId);
    await updateDoc(noteRef, {
      order: newOrder,
      updatedAt: Timestamp.now()
    });
    return noteId;
  } catch (error) {
    logError("Error updating note order", error);
    throw error;
  }
};

// Restore categories from existing notes
export const restoreCategoriesFromNotes = async (userId) => {
  try {
    log('Starting category restoration from notes');
    
    // Get all notes for the user
    const q = query(
      notesCollection,
      where("userId", "==", userId)
    );
    const querySnapshot = await getDocs(q);
    
    // Extract unique category names from notes
    const categoryNames = new Set();
    querySnapshot.forEach((doc) => {
      const noteData = doc.data();
      if (noteData.category && noteData.category.trim() !== '') {
        categoryNames.add(noteData.category.trim());
      }
    });
    
    log(`Found ${categoryNames.size} unique categories in notes`);
    
    // Get existing categories to preserve colors if they exist
    const userRef = doc(usersCollection, userId);
    const userDoc = await getDoc(userRef);
    const existingCategories = userDoc.exists() 
      ? (userDoc.data().categories || []) 
      : [];
    
    // Create a map of existing category colors
    const categoryColorMap = new Map();
    existingCategories.forEach(cat => {
      categoryColorMap.set(cat.name, cat.color);
    });
    
    // Default color palette for categories
    const defaultColors = [
      'bg-blue-200',
      'bg-green-200',
      'bg-yellow-200',
      'bg-purple-200',
      'bg-pink-200',
      'bg-indigo-200',
      'bg-red-200',
      'bg-orange-200',
      'bg-teal-200',
      'bg-cyan-200'
    ];
    
    // Build restored categories array
    const restoredCategories = [];
    let colorIndex = 0;
    
    // Always include "All Notes" first
    restoredCategories.push({ name: 'All Notes', color: 'bg-gray-300' });
    
    // Add categories from notes
    categoryNames.forEach(categoryName => {
      // Skip "All Notes" if it appears in notes
      if (categoryName === 'All Notes') return;
      
      // Use existing color if available, otherwise assign from default palette
      const color = categoryColorMap.get(categoryName) || defaultColors[colorIndex % defaultColors.length];
      restoredCategories.push({ name: categoryName, color });
      colorIndex++;
    });
    
    // Always include "Uncategorized" last
    if (!categoryNames.has('Uncategorized')) {
      restoredCategories.push({ name: 'Uncategorized', color: 'bg-gray-300' });
    }
    
    // Update user's categories
    await updateUserCategories(userId, restoredCategories);
    
    log(`Successfully restored ${restoredCategories.length} categories`);
    return restoredCategories;
  } catch (error) {
    logError("Error restoring categories from notes", error);
    throw error;
  }
};
